import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { AdminTabs } from "../components/AdminTabs";
import {
  Button,
  Card,
  ErrorNote,
  Spinner,
  StatusBadge,
  Tag,
  type StatusTone,
} from "../components/primitives";
import { api } from "../lib/api";
import { fullStamp, money, stamp } from "../lib/format";
import type { MailMessage, MailPage } from "../lib/types";

const OUTCOME_TONE: Record<string, StatusTone> = {
  handled: "good",
  sent: "good",
  dropped: "neutral",
  received: "neutral",
  unsent: "warning",
  failed: "critical",
};

/**
 * What came in to ask@ and the forwarded addresses, and what went out.
 *
 * The audit trail says *that* a proposal was made; this says what was
 * written, both ways — "what exactly did this person receive?" is answered by
 * opening the thread. The text is deleted after the retention period and the
 * headers stay, so an old row reads as deleted rather than as empty.
 *
 * A list and a thread, by `?proposal=`, so a thread is a link somebody can
 * send.
 */
export function AdminMail() {
  const [params, setParams] = useSearchParams();
  const thread = params.get("proposal");
  return (
    <div className="space-y-4">
      <AdminTabs />
      {thread ? (
        <Thread id={thread} onClose={() => setParams({})} />
      ) : (
        <Journal open={(id) => setParams({ proposal: id })} />
      )}
    </div>
  );
}

function Journal({ open }: { open: (proposal: string) => void }) {
  const [direction, setDirection] = useState("");
  const [outcome, setOutcome] = useState("");
  const [before, setBefore] = useState<string | undefined>();
  const mail = useQuery({
    queryKey: ["admin-mail", direction, outcome, before],
    queryFn: () => api.adminMail({ direction, outcome, before }),
  });
  const messages = mail.data?.messages ?? [];

  return (
    <Card
      title="Mail"
      action={<Kept page={mail.data} />}
    >
      <div className="flex flex-wrap items-end gap-3 border-b border-line p-4">
        <div>
          <label className="field-label" htmlFor="mail-direction">
            Direction
          </label>
          <select
            id="mail-direction"
            className="field"
            value={direction}
            onChange={(e) => {
              setDirection(e.target.value);
              setBefore(undefined);
            }}
          >
            <option value="">In and out</option>
            <option value="in">In</option>
            <option value="out">Out</option>
          </select>
        </div>
        <div>
          <label className="field-label" htmlFor="mail-outcome">
            Outcome
          </label>
          <select
            id="mail-outcome"
            className="field"
            value={outcome}
            onChange={(e) => {
              setOutcome(e.target.value);
              setBefore(undefined);
            }}
          >
            <option value="">Any</option>
            <option value="handled">Handled</option>
            <option value="dropped">Dropped</option>
            <option value="sent">Sent</option>
            <option value="failed">Failed</option>
            <option value="unsent">Not sent</option>
          </select>
        </div>
      </div>

      {mail.isLoading && <Spinner />}
      {mail.error && <ErrorNote error={mail.error} />}
      {!mail.isLoading && messages.length === 0 && (
        <p className="px-4 py-6 text-sm text-muted">
          {direction || outcome
            ? "Nothing matches that."
            : "No mail yet. Requests to ask@ and everything sent back appear here."}
        </p>
      )}

      <ul className="divide-y divide-line">
        {messages.map((message) => (
          <Row key={message.id} message={message} open={open} />
        ))}
      </ul>

      {(before || mail.data?.next) && (
        <div className="flex gap-3 border-t border-line px-4 py-3">
          {before && (
            <Button size="sm" variant="ghost" onClick={() => setBefore(undefined)}>
              Newest
            </Button>
          )}
          {mail.data?.next && (
            <Button size="sm" onClick={() => setBefore(mail.data?.next ?? undefined)}>
              Older
            </Button>
          )}
        </div>
      )}
    </Card>
  );
}

function Row({ message, open }: { message: MailMessage; open: (proposal: string) => void }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <li className="px-4 py-2.5 text-sm">
      <Head message={message} />
      <div className="mt-0.5 flex flex-wrap gap-x-3 pl-[9.75rem] text-xs text-muted">
        <span className="font-mono">
          {message.sender || "—"} → {message.recipient || "—"}
        </span>
        {message.note && <span>{message.note}</span>}
        {(message.body || message.deleted) && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="underline"
          >
            {expanded ? "less" : "text"}
          </button>
        )}
        {message.proposal_id && (
          <button
            type="button"
            onClick={() => open(message.proposal_id!)}
            className="text-accent"
          >
            open thread
          </button>
        )}
      </div>
      {expanded && <Body message={message} />}
    </li>
  );
}

function Thread({ id, onClose }: { id: string; onClose: () => void }) {
  const mail = useQuery({
    queryKey: ["admin-mail-thread", id],
    queryFn: () => api.adminMail({ proposal: id }),
  });
  const info = mail.data?.thread;
  return (
    <Card
      title={
        <div className="flex flex-wrap items-center gap-3">
          <button type="button" onClick={onClose} className="text-sm text-ink-2 underline">
            All mail
          </button>
          <h2 className="text-sm font-semibold">Thread</h2>
        </div>
      }
      action={<Kept page={mail.data} />}
    >
      {mail.isLoading && <Spinner />}
      {mail.error && <ErrorNote error={mail.error} />}
      {info && (
        <div className="space-y-1 border-b border-line px-4 py-3 text-sm">
          <div className="flex flex-wrap items-center gap-3">
            <StatusBadge tone={info.status === "ordered" ? "good" : "neutral"}>
              {info.status}
            </StatusBadge>
            <span className="font-mono text-xs text-muted">{info.owner ?? "unknown owner"}</span>
            <span className="font-mono text-xs tabular-nums text-muted">
              proposal cost {money(info.cost_usd)}
            </span>
            {info.run_id && (
              <Link
                to={`/engagements/${encodeURIComponent(info.run_id)}`}
                className="text-xs text-accent"
              >
                open run
              </Link>
            )}
            {info.parent_id && (
              <Link to={`/admin/mail?proposal=${info.parent_id}`} className="text-xs text-accent">
                the proposal it corrects
              </Link>
            )}
          </div>
          {info.options.length > 0 && (
            <ol className="list-decimal pl-5 text-ink-2">
              {info.options.map((title, index) => (
                <li key={index} className={info.chosen === index + 1 ? "font-medium text-ink" : ""}>
                  {title}
                </li>
              ))}
            </ol>
          )}
        </div>
      )}
      <ul className="divide-y divide-line">
        {(mail.data?.messages ?? []).map((message) => (
          <li key={message.id} className="px-4 py-3 text-sm">
            <Head message={message} />
            <div className="mt-0.5 pl-[9.75rem] font-mono text-xs text-muted">
              {message.sender || "—"} → {message.recipient || "—"}
              {message.note && <span className="ml-3 font-sans">{message.note}</span>}
            </div>
            <Body message={message} />
          </li>
        ))}
      </ul>
    </Card>
  );
}

function Head({ message }: { message: MailMessage }) {
  const when = new Date(message.created_at);
  return (
    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
      <time
        className="w-36 shrink-0 font-mono text-xs tabular-nums text-muted"
        dateTime={message.created_at}
        title={fullStamp(when)}
      >
        {stamp(when)}
      </time>
      <Tag strong={message.direction === "in"}>{message.direction}</Tag>
      <span className="shrink-0 font-mono text-xs text-muted">
        {message.route}@ · {message.kind}
      </span>
      <span className="min-w-0 flex-1 truncate text-ink">
        {message.subject || "(no subject)"}
      </span>
      {message.verified === false && <StatusBadge tone="warning">unverified</StatusBadge>}
      <StatusBadge tone={OUTCOME_TONE[message.outcome] ?? "neutral"}>{message.outcome}</StatusBadge>
    </div>
  );
}

function Body({ message }: { message: MailMessage }) {
  if (message.deleted) {
    return (
      <p className="mt-2 pl-[9.75rem] text-xs text-muted">
        Text deleted after the retention period ({message.chars.toLocaleString()} characters).
      </p>
    );
  }
  if (!message.body) return null;
  return (
    <pre className="mt-2 overflow-x-auto whitespace-pre-wrap rounded-sm border border-line bg-surface-2 p-3 font-sans text-xs text-ink-2 sm:ml-[9.75rem]">
      {message.body}
    </pre>
  );
}

function Kept({ page }: { page: MailPage | undefined }) {
  if (!page) return null;
  return (
    <span className="text-xs text-muted">
      {page.spend.proposals} proposal{page.spend.proposals === 1 ? "" : "s"} in{" "}
      {page.spend.days} days ·{" "}
      <span className="font-mono tabular-nums">{money(page.spend.cost_usd)}</span> in model
      calls · text kept {page.keep_days} days
    </span>
  );
}
